// Маркеры карты — визуально как в макете (строки 146–161, 165–246, mapModel 1061–1066).
// Каждый маркер — обёртка 0×0, якорь в точке координаты; одинаково для MapLibre и фолбэка.
import { Building2, ChefHat, House, MessageCircle, Navigation, Store, type LucideIcon } from 'lucide-react'
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react'
import type { BubblePinData, MapPin, PersonPinData, PlacePinData, TilePinData, VertexPinData } from './types'

/** Ключевые кадры пульса (React 19 поднимает <style> в head и дедуплицирует по href). */
export function MapKeyframes() {
  return (
    <style href="dalat-map-keyframes" precedence="default">
      {'@keyframes dalat-map-pulse{0%{transform:scale(.6);opacity:.9}100%{transform:scale(2.4);opacity:0}}' +
        '@media (prefers-reduced-motion:reduce){.dalat-map-pulse{animation:none!important}}'}
    </style>
  )
}

const anchor: CSSProperties = { position: 'relative', width: 0, height: 0 }

function Clickable({ onClick, label, children, style }: { onClick?: () => void; label?: string; children: ReactNode; style: CSSProperties }) {
  if (!onClick) return <div style={style}>{children}</div>
  const click = (e: MouseEvent) => {
    e.stopPropagation()
    onClick()
  }
  const key = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }
  return (
    <div role="button" tabIndex={0} aria-label={label} onClick={click} onKeyDown={key} style={{ ...style, cursor: 'pointer' }}>
      {children}
    </div>
  )
}

/** Плитка на карте — 28px с кодом 12px, а зона нажатия — 44×44 (минимальная цель касания на мобильных), плитка по центру. */
const PIN_TILE = 28
const PIN_FONT = 12
const HIT = 44
const hit: CSSProperties = {
  position: 'absolute',
  left: -HIT / 2,
  top: -HIT / 2,
  width: HIT,
  height: HIT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

/** С label — круг с инициалами (карта диспетчерской): размер плитки мастерской, без обводки, зона нажатия 44px. */
export function CourierPin({ label, muted, onClick }: { label?: string; muted?: boolean; onClick?: () => void } = {}) {
  if (label) {
    const bg = muted ? 'var(--muted2)' : 'var(--pri)'
    return (
      <div style={anchor} data-pin="courier" title={label}>
        {!muted && (
          <div
            className="dalat-map-pulse"
            style={{
              position: 'absolute',
              left: -20,
              top: -20,
              width: 40,
              height: 40,
              borderRadius: 20,
              background: bg,
              animation: 'dalat-map-pulse 1.8s ease-out infinite',
              pointerEvents: 'none',
            }}
          />
        )}
        <Clickable onClick={onClick} label={label} style={hit}>
          <div
            style={{
              width: PIN_TILE,
              height: PIN_TILE,
              borderRadius: PIN_TILE / 2,
              background: bg,
              color: muted ? 'var(--fg)' : '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,.35)',
              font: `800 ${PIN_FONT}px/1 Onest, system-ui, sans-serif`,
              letterSpacing: '.02em',
              flexShrink: 0,
            }}
          >
            {label}
          </div>
        </Clickable>
      </div>
    )
  }
  return (
    <div style={anchor} data-pin="courier">
      <div
        className="dalat-map-pulse"
        style={{
          position: 'absolute',
          left: -17,
          top: -17,
          width: 34,
          height: 34,
          borderRadius: 17,
          background: 'var(--pri)',
          animation: 'dalat-map-pulse 1.8s ease-out infinite',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: -8,
          top: -8,
          width: 16,
          height: 16,
          borderRadius: 8,
          background: 'var(--pri)',
          boxShadow: '0 0 0 3px #fff',
        }}
      />
    </div>
  )
}

export function ShopPin({ label, onClick }: { label?: string; onClick?: () => void }) {
  return (
    <div style={anchor} data-pin="shop">
      <Clickable
        onClick={onClick}
        label={label}
        style={{
          position: 'absolute',
          left: -17,
          top: -17,
          width: 34,
          height: 34,
          borderRadius: 9,
          background: 'var(--fg)',
          color: 'var(--bg)',
          border: '2px solid var(--bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,.35)',
          boxSizing: 'border-box',
        }}
      >
        <Store size={18} aria-hidden />
      </Clickable>
    </div>
  )
}

/** Клиент — «капля» с острием в точке; caption — подпись-плашка под острием (номер заказа), кликабельна вместе с каплей. */
export function ClientPin({ label, caption, onClick }: { label?: string; caption?: string; onClick?: () => void }) {
  return (
    <div style={anchor} data-pin="client" title={caption}>
      <Clickable
        onClick={onClick}
        label={caption ?? label}
        style={{
          position: 'absolute',
          left: -15,
          top: -37,
          width: 30,
          height: 30,
          borderRadius: '15px 15px 15px 3px',
          background: 'var(--info)',
          color: '#fff',
          border: '2px solid #fff',
          transform: 'rotate(-45deg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,.35)',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ transform: 'rotate(45deg)', display: 'flex', font: '700 14px/1 Onest, system-ui, sans-serif' }}>{label ?? ''}</span>
      </Clickable>
      {caption && (
        <Clickable
          onClick={onClick}
          label={caption}
          style={{
            position: 'absolute',
            left: 0,
            top: 4,
            transform: 'translateX(-50%)',
            height: 20,
            padding: '0 6px',
            borderRadius: 6,
            background: 'var(--info)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 6px rgba(0,0,0,.35)',
            font: '700 11px/1 Onest, system-ui, sans-serif',
            letterSpacing: '.02em',
          }}
        >
          {caption}
        </Clickable>
      )}
    </div>
  )
}

/** Лавка на карте ленты: оранжевый квадрат 44px с числом свободных заказов и подписью. */
/** Плитка мастерской: залитый цветом квадрат со скруглёнными углами и кодом (SH, OP…); count > 0 — бейдж. */
export function ShopTile({
  code,
  color,
  count = 0,
  size = 40,
  fontSize = Math.round(size * 0.36),
}: {
  code: string
  color: string
  count?: number
  size?: number
  /** Размер кода; по умолчанию от size. На карте плитка компактная, а текст прежний — меньше пустого места. */
  fontSize?: number
}) {
  const badge = Math.max(18, Math.round(size * 0.55))
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: Math.round(size / 4),
        background: color,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,.35)',
        font: `800 ${fontSize}px/1 Onest, system-ui, sans-serif`,
        letterSpacing: '.02em',
        flexShrink: 0,
      }}
    >
      {code}
      {count > 0 && (
        <span
          style={{
            position: 'absolute',
            top: -Math.round(badge / 3),
            right: -Math.round(badge / 3),
            minWidth: badge,
            height: badge,
            padding: '0 4px',
            boxSizing: 'border-box',
            borderRadius: badge / 2,
            background: 'var(--fg)',
            color: 'var(--bg)',
            font: `800 ${Math.round(badge * 0.55)}px/${badge}px Onest, system-ui, sans-serif`,
            textAlign: 'center',
          }}
        >
          {count}
        </span>
      )}
    </div>
  )
}

/** Мастерская на карте ленты: плитка с кодом и числом свободных заказов; имя — только для скринридера. */
export function ShopCountPin({ count, name, code, color = 'var(--pri)', onClick }: { count: number; name: string; code: string; color?: string; onClick?: () => void }) {
  return (
    <div style={anchor} data-pin="shopCount">
      <Clickable onClick={onClick} label={name} style={hit}>
        <ShopTile code={code} color={color} count={count} size={PIN_TILE} fontSize={PIN_FONT} />
      </Clickable>
    </div>
  )
}

/** Мастерская без свободных заказов: та же плитка без бейджа; без code — серый квадрат 16px. */
export function ShopIdlePin({ name, code, color, onClick }: { name?: string; code?: string; color?: string; onClick?: () => void }) {
  return (
    <div style={anchor} data-pin="shopIdle">
      {code ? (
        <Clickable onClick={onClick} label={name} style={hit}>
          <ShopTile code={code} color={color ?? 'var(--mfg)'} size={PIN_TILE} fontSize={PIN_FONT} />
        </Clickable>
      ) : (
        <Clickable
          onClick={onClick}
          label={name}
          style={{ position: 'absolute', left: -8, top: -8, width: 16, height: 16, borderRadius: 4, background: 'var(--muted2)' }}
        >
          {null}
        </Clickable>
      )}
    </div>
  )
}

const PLACE: Record<PlacePinData['kind'], { bg: string; Icon: LucideIcon }> = {
  home: { bg: 'var(--info)', Icon: House },
  house: { bg: 'var(--info)', Icon: Building2 },
  me: { bg: 'var(--info)', Icon: Navigation },
  kitchen: { bg: 'var(--shop-1)', Icon: ChefHat },
}

/** Плитка клиентской карты (макет dalat-kit pinEl): 38px r13, градиент цвета, белая иконка 19px, центр — в точке. */
export function PlacePin({ kind, name, onClick }: { kind: PlacePinData['kind']; name?: string; onClick?: () => void }) {
  const { bg, Icon } = PLACE[kind]
  return (
    <div style={anchor} data-pin={kind} title={name}>
      <Clickable onClick={onClick} label={name} style={hit}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 13,
            background: `linear-gradient(160deg,color-mix(in oklab,${bg} 82%,#fff),${bg})`,
            color: '#fff',
            border: '1px solid rgba(255,255,255,.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `inset 0 1px 0 rgba(255,255,255,.28),0 6px 16px color-mix(in oklab,${bg} 45%,transparent),0 2px 6px rgba(0,0,0,.35)`,
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <Icon size={19} strokeWidth={2} aria-hidden style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.2))' }} />
        </div>
      </Clickable>
    </div>
  )
}

/** Пузырь с текстом (цена объявления): h30 r15, var(--card) / active var(--pri), центр — в точке. */
export function LabelPin({ text, active, onClick }: { text: string; active?: boolean; onClick?: () => void }) {
  return (
    <div style={anchor} data-pin="label" data-active={active || undefined}>
      <Clickable
        onClick={onClick}
        label={text}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          transform: 'translate(-50%,-50%)',
          height: 30,
          padding: '0 10px',
          borderRadius: 15,
          background: active ? 'var(--pri)' : 'var(--card)',
          color: active ? '#fff' : 'var(--fg)',
          border: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(0,0,0,.3)',
          font: '700 13px/1 Onest, system-ui, sans-serif',
          boxSizing: 'border-box',
        }}
      >
        {text}
      </Clickable>
    </div>
  )
}

/** Выбранная метка (макет Map View 2a): крупнее и с кольцом цвета текста. */
const SELECTED_SHADOW = '0 0 0 3px var(--fg),0 8px 20px rgba(0,0,0,.5)'
const pop: CSSProperties = { transition: 'transform .25s cubic-bezier(.34,1.32,.5,1)' }

function tileShadow(c: string): string {
  return `inset 0 1px 0 rgba(255,255,255,.28),0 6px 16px color-mix(in oklab,${c} 45%,transparent),0 2px 6px rgba(0,0,0,.35)`
}

/** Человек: круг 38px с фото или инициалами на цвете; зелёная точка — сейчас на карте. */
export function PersonPin({ initials, photo, color, name, selected, onClick }: Omit<PersonPinData, 'kind' | 'lat' | 'lng'>) {
  return (
    <div style={anchor} data-pin="person" data-selected={selected || undefined} title={name}>
      <Clickable onClick={onClick} label={name} style={hit}>
        <div
          style={{
            ...pop,
            position: 'relative',
            width: 38,
            height: 38,
            borderRadius: 19,
            background: color,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            font: '800 13px/1 Onest, system-ui, sans-serif',
            letterSpacing: '.02em',
            boxShadow: selected ? SELECTED_SHADOW : tileShadow(color),
            transform: `scale(${selected ? 1.22 : 1})`,
            flexShrink: 0,
          }}
        >
          {photo ? (
            <img
              src={photo}
              alt=""
              draggable={false}
              style={{ position: 'absolute', inset: 0, width: 38, height: 38, borderRadius: 19, objectFit: 'cover' }}
            />
          ) : (
            initials
          )}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 28,
              top: 27,
              width: 12,
              height: 12,
              borderRadius: 6,
              background: 'var(--suc)',
              border: '2px solid var(--bg)',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </Clickable>
    </div>
  )
}

/** Плитка 38px r13 с градиентом цвета и белой иконкой; time — белая плашка над плиткой (время события). */
export function TilePin({ icon: Icon, color, time, name, selected, badge: Badge, badgeColor, dim, onClick }: Omit<TilePinData, 'kind' | 'lat' | 'lng'>) {
  return (
    <div style={{ ...anchor, opacity: dim ? 0.55 : 1 }} data-pin="tile" data-selected={selected || undefined} title={name}>
      {time && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: selected ? -46 : -42,
            transform: 'translateX(-50%)',
            height: 20,
            padding: '0 7px',
            borderRadius: 10,
            background: 'var(--fg)',
            color: 'var(--bg)',
            font: '800 11px/1 Onest, system-ui, sans-serif',
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 6px rgba(0,0,0,.35)',
            pointerEvents: 'none',
          }}
        >
          {time}
        </div>
      )}
      <Clickable onClick={onClick} label={name} style={hit}>
        <div
          style={{
            ...pop,
            position: 'relative',
            width: 38,
            height: 38,
            borderRadius: 13,
            background: `linear-gradient(160deg,color-mix(in oklab,${color} 82%,#fff),${color})`,
            color: '#fff',
            border: '1px solid rgba(255,255,255,.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: selected ? SELECTED_SHADOW : tileShadow(color),
            transform: `scale(${selected ? 1.22 : 1})`,
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <Icon size={19} strokeWidth={2} aria-hidden style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.2))' }} />
          {Badge && (
            <span
              data-pin-badge
              style={{
                position: 'absolute',
                right: -7,
                top: -7,
                width: 19,
                height: 19,
                borderRadius: 10,
                background: 'var(--bg)',
                color: badgeColor ?? 'var(--fg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,.35)',
              }}
            >
              <Badge size={11} strokeWidth={2.5} aria-hidden />
            </span>
          )}
        </div>
      </Clickable>
    </div>
  )
}

/** Вершина рисуемого маршрута: круг с белой обводкой, у остановки — её номер. */
export function VertexPin({ color, size = 18, label, selected, onClick }: Omit<VertexPinData, 'kind' | 'lat' | 'lng'>) {
  return (
    <div style={anchor} data-pin="vertex" data-selected={selected || undefined}>
      <Clickable onClick={onClick} label={label} style={hit}>
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: color,
            border: '3px solid #fff',
            boxShadow: selected ? '0 0 0 4px var(--fg),0 3px 8px rgba(0,0,0,.4)' : '0 2px 6px rgba(0,0,0,.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            font: '800 11px/1 Onest, system-ui, sans-serif',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          {label}
        </div>
      </Clickable>
    </div>
  )
}

/** Комментарий в точке маршрута: облачко со счётчиком над точкой-якорем. */
export function BubblePin({ count, name, onClick }: Omit<BubblePinData, 'kind' | 'lat' | 'lng'>) {
  return (
    <div style={anchor} data-pin="bubble" title={name}>
      <Clickable
        onClick={onClick}
        label={name}
        style={{
          position: 'absolute',
          left: -5,
          top: -38,
          height: 32,
          padding: '0 10px 0 8px',
          borderRadius: '16px 16px 16px 4px',
          background: 'var(--card)',
          color: 'var(--fg)',
          border: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          font: '800 13px/1 Onest, system-ui, sans-serif',
          boxShadow: '0 4px 12px rgba(0,0,0,.3)',
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
        }}
      >
        <MessageCircle size={16} aria-hidden style={{ color: 'var(--pri)' }} />
        <span>{count}</span>
      </Clickable>
      <div style={{ position: 'absolute', left: -4, top: -4, width: 8, height: 8, borderRadius: 4, background: 'var(--pri)', border: '2px solid var(--bg)', boxSizing: 'border-box' }} />
    </div>
  )
}

/** Группа «+N»: круг 44px цвета карточки, под числом — точки типов внутри. */
export function ClusterPin({ count, colors, onClick }: { count: number; colors: readonly string[]; onClick?: () => void }) {
  return (
    <div style={anchor} data-pin="cluster">
      <Clickable onClick={onClick} label={`+${count}`} style={hit}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            background: 'var(--card)',
            color: 'var(--fg)',
            border: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,.35)',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <span style={{ font: '800 13px/14px Onest, system-ui, sans-serif' }}>+{count}</span>
          <span style={{ display: 'flex', gap: 2 }}>
            {colors.map((c) => (
              <span key={c} style={{ width: 4, height: 4, borderRadius: 2, background: c }} />
            ))}
          </span>
        </div>
      </Clickable>
    </div>
  )
}

export function PinView({ pin }: { pin: MapPin }) {
  switch (pin.kind) {
    case 'courier':
      return <CourierPin label={pin.label} muted={pin.muted} onClick={pin.onClick} />
    case 'shop':
      return <ShopPin label={pin.label} onClick={pin.onClick} />
    case 'client':
      return <ClientPin label={pin.label} caption={pin.caption} onClick={pin.onClick} />
    case 'shopCount':
      return <ShopCountPin count={pin.count} name={pin.name} code={pin.code} color={pin.color} onClick={pin.onClick} />
    case 'shopIdle':
      return <ShopIdlePin name={pin.name} code={pin.code} color={pin.color} onClick={pin.onClick} />
    case 'home':
    case 'house':
    case 'me':
    case 'kitchen':
      return <PlacePin kind={pin.kind} name={pin.name} onClick={pin.onClick} />
    case 'label':
      return <LabelPin text={pin.text} active={pin.active} onClick={pin.onClick} />
    case 'person':
      return <PersonPin initials={pin.initials} photo={pin.photo} color={pin.color} name={pin.name} selected={pin.selected} onClick={pin.onClick} />
    case 'tile':
      return (
        <TilePin
          icon={pin.icon}
          color={pin.color}
          time={pin.time}
          name={pin.name}
          selected={pin.selected}
          badge={pin.badge}
          badgeColor={pin.badgeColor}
          dim={pin.dim}
          onClick={pin.onClick}
        />
      )
    case 'vertex':
      return <VertexPin color={pin.color} size={pin.size} label={pin.label} selected={pin.selected} onClick={pin.onClick} />
    case 'bubble':
      return <BubblePin count={pin.count} name={pin.name} onClick={pin.onClick} />
    case 'cluster':
      return <ClusterPin count={pin.count} colors={pin.colors} onClick={pin.onClick} />
  }
}

/** Порядок отрисовки: серые → лавки → клиенты → курьер сверху. */
export const PIN_Z: Record<MapPin['kind'], number> = {
  shopIdle: 1,
  house: 2,
  label: 2,
  kitchen: 2,
  shop: 2,
  shopCount: 3,
  home: 4,
  client: 4,
  me: 5,
  courier: 5,
  tile: 3,
  cluster: 3,
  person: 4,
  vertex: 7,
  bubble: 8,
}

/** Порядок пина: активный пузырь и выбранная метка — поверх всех. */
export function pinZ(p: MapPin): number {
  if (p.kind === 'label' && p.active) return 9
  if ((p.kind === 'tile' || p.kind === 'person' || p.kind === 'vertex') && p.selected) return 9
  return PIN_Z[p.kind]
}
